import type { FilterDef, ManualDashboardPayload } from '../../types/manualDashboard';

/**
 * What the agent is told the analyst is looking at.
 *
 * Includes the chart selections even though they never reach the server —
 * metric and dimension are chosen client-side, so without this the agent
 * would not know which metric is on screen when someone asks "why did this
 * drop?".
 *
 * Filters are included as the current scope. The prompt tells the agent it may
 * widen them to answer a comparison, provided it says which scope it used.
 */
export function buildBoardSurfaceContext(params: {
  dashboardId: string;
  payload: ManualDashboardPayload;
  trendMetric: string | null;
  segmentMetric: string | null;
  dimension: string | null;
  filterDefs: FilterDef[];
  /** Groupings a chart or table was switched to, where it differs from the board's. */
  sectionGrains?: Record<string, string>;
}): Record<string, unknown> {
  const { dashboardId, payload, trendMetric, segmentMetric, dimension, filterDefs, sectionGrains } = params;

  return {
    type: 'manual_dashboard',
    dashboard_id: dashboardId,
    name: payload.spec.name,
    cohort_label: payload.spec.cohort_label,
    range: payload.range,
    grain: payload.grain,
    section_grains: sectionGrains ?? {},
    period_labels: payload.periods.map((p) => p.label),
    filters: payload.selected_filters,
    dimension: dimension ?? payload.selected_dimension,
    trend_metric: trendMetric,
    segment_metric: segmentMetric,
    total_cases: payload.meta.total_cases,
    as_of: payload.meta.as_of,
    metrics: payload.metrics,
    dimensions: payload.dimensions,
    // Names the agent may set, not just the ones currently set.
    filter_defs: filterDefs.map((f) => ({
      key: f.key,
      label: f.label,
      ...(f.default?.length ? { default: f.default } : {}),
      ...(f.kind ? { kind: f.kind, options: f.options.map((o) => o.value) } : {}),
    })),
    // The blocks carry the headline figures, the funnel, and — importantly —
    // the per-row caveats, so the agent qualifies the proxy and thin figures
    // the way the table does instead of reporting them as plain facts.
    blocks: payload.blocks,
  };
}
